/** @file Converts frame-based document timelines to export timelines (seconds, curves, per-influence deform). */
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import type { Doc, DocIndex, Pose2DJson } from '@pose2d/format/types';
import { boneTimelines } from './boneTimelines';
import { deformTimelines } from './deformTimelines';
import { drawOrderTimeline } from './drawOrderTimeline';
import { eventTimeline } from './eventTimeline';
import { ikTimelines } from './ikTimelines';
import { lastKeyFrame } from './lastKeyFrame';
import { slotTimelines } from './slotTimelines';

type AnimationJson = Pose2DJson['animations'][number];

/** Returns export animations in name order (entriesByName, R12-01). */
export function compileAnimations(doc: Doc, index: DocIndex, maps: { boneIndex: Map<string, number>; slotIndex: Map<string, number>; ikIndex: Map<string, number>; attachmentIndex: Map<string, number>; eventIndex: Map<string, number> }): Pose2DJson['animations'] {
  const fps = doc.meta.fps;
  return entriesByName(doc.animations).map(([id, anim]): AnimationJson => {
    const at = `animations.${id}`;
    const drawOrder = drawOrderTimeline(anim, fps, maps.slotIndex, at);
    const events = eventTimeline(anim, fps, maps.eventIndex, at);
    return {
      name: anim.name,
      duration: (anim.durationFrames ?? lastKeyFrame(anim)) / fps,
      timelines: [
        ...boneTimelines(anim, fps, maps.boneIndex, at),
        ...slotTimelines(anim, fps, maps.slotIndex, at),
        ...ikTimelines(anim, fps, maps.ikIndex, at),
        ...deformTimelines(doc, anim, fps, maps.attachmentIndex, at),
        ...(drawOrder === null ? [] : [drawOrder]),
        ...(events === null ? [] : [events]),
      ],
    };
  });
}
